// OWNER: FRONTEND-ADMIN. SPEC §15.5 Campaigns: list with counts, create from template, full script editor,
// windows, limits, status (draft → live gated), setters, clone to next week, CSV import, members, lookups, briefs.
// Setters get a read-only view of the campaigns they're assigned to.
import { useEffect, useMemo, useState } from 'preact/hooks';
import type { Campaign, CampaignScript, CampaignStatus, CampaignTemplate, Country } from '../../../src/shared/api';
import { errorMessage } from '../api';
import { navigate } from '../app';
import { Empty, ErrorBanner, Modal, fmtDateTime, toast } from '../components/ui';
import { useMe } from '../me';
import { adminApi } from '../admin/endpoints';
import { ScriptEditor, ScriptView } from '../admin/ScriptEditor';
import { ImportPanel } from '../admin/ImportPanel';
import { BriefRunner, LookupRunner } from '../admin/Runners';
import { CalendlySlugField, MembersTable, SettersPanel, WindowsEditor } from '../admin/CampaignParts';
import { Check, ConfirmModal, Field, ProblemList, StatusChip, Tabs, inputValue, useLoad, useUnsavedWarning } from '../admin/ui';
import {
  DEFAULT_CALENDLY_SLUG, activeSetterIds, calendlySlugProblems, goLiveProblems, houseRuleProblems, isBlockedHealthcareCampaign, normalizeCalendlySlug,
  normalizeScript, scriptProblems, templateCalendlySlug,
} from '../admin/validate';
import { OPS_TZ, nextMonday, prettyYmd, ymdInTz } from '../admin/dates';

export function Campaigns(props: { params: Record<string, string> }) {
  const id = props.params.rest ? Number(props.params.rest.split('/')[0]) : null;
  if (id !== null && Number.isInteger(id) && id > 0) return <CampaignDetail id={id} />;
  return <CampaignList />;
}

// ---------------------------------------------------------------- list

const STATUS_FILTERS: Array<{ key: '' | CampaignStatus; label: string }> = [
  { key: '', label: 'All' }, { key: 'live', label: 'Live' }, { key: 'draft', label: 'Draft' }, { key: 'paused', label: 'Paused' }, { key: 'done', label: 'Done' },
];

function CampaignList() {
  const me = useMe();
  const isAdmin = me.role === 'admin';
  const [filter, setFilter] = useState<'' | CampaignStatus>('');
  const [creating, setCreating] = useState(false);
  const campaigns = useLoad(() => adminApi.campaigns(), []);
  const shown = (campaigns.data ?? []).filter((c) => !filter || c.status === filter)
    .sort((a, b) => (b.weekStart ?? '').localeCompare(a.weekStart ?? '') || b.id - a.id);

  return (
    <div class="adm-page">
      <div class="adm-head">
        <div>
          <h1>Campaigns</h1>
          <div class="adm-sub">{isAdmin ? 'One campaign per niche × offer × week. Build it, assign setters, import leads, then go live.' : 'Campaigns you are assigned to.'}</div>
        </div>
        {isAdmin ? <button class="btn btn-primary" onClick={() => setCreating(true)}>New campaign</button> : null}
      </div>
      <div class="adm-toolbar">
        {STATUS_FILTERS.map((f) => (
          <button class={`btn btn-sm ${filter === f.key ? 'btn-ink' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}{f.key ? ` (${(campaigns.data ?? []).filter((c) => c.status === f.key).length})` : ''}
          </button>
        ))}
      </div>
      <ErrorBanner error={campaigns.error} />
      {campaigns.loading && !campaigns.data ? <div class="muted">Loading campaigns…</div> : null}
      {campaigns.data && shown.length === 0 ? (
        <Empty>{campaigns.data.length === 0 ? (isAdmin ? 'No campaigns yet. Create one from a template.' : 'You are not assigned to a campaign yet. Ask PJ.') : 'No campaigns with this status.'}</Empty>
      ) : null}
      {shown.length ? (
        <div class="adm-table-wrap">
          <table>
            <thead><tr>
              <th>Campaign</th><th>Status</th><th>Week of</th><th>Geo</th><th>Niche · offer</th><th class="num">Setters</th>
              <th class="num">Leads</th><th class="num">New</th><th class="num">In progress</th><th class="num">Callback</th><th class="num">Meeting</th><th class="num">Closed</th>
            </tr></thead>
            <tbody>
              {shown.map((c) => (
                <tr class="adm-clickable" onClick={() => navigate(`/campaigns/${c.id}`)}>
                  <td><a href={`/campaigns/${c.id}`} onClick={(e) => { e.preventDefault(); navigate(`/campaigns/${c.id}`); }}><b>{c.name}</b></a></td>
                  <td><StatusChip status={c.status} /></td>
                  <td>{c.weekStart ? prettyYmd(c.weekStart) : '—'}</td>
                  <td>{c.country}{c.geoLabel ? ` · ${c.geoLabel}` : ''}</td>
                  <td>{c.niche} · {c.offer}</td>
                  <td class="num">{c.userIds?.length ?? 0}</td>
                  <td class="num">{c.counts?.total ?? '—'}</td>
                  <td class="num">{c.counts?.new ?? '—'}</td>
                  <td class="num">{c.counts?.inProgress ?? '—'}</td>
                  <td class="num">{c.counts?.callback ?? '—'}</td>
                  <td class="num">{c.counts?.meeting ?? '—'}</td>
                  <td class="num">{c.counts?.closed ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {creating ? <CreateCampaignModal onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function CreateCampaignModal(props: { onClose: () => void }) {
  const templates = useLoad(() => adminApi.templates(), []);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [geoLabel, setGeoLabel] = useState('');
  const [weekStart, setWeekStart] = useState(nextMonday(ymdInTz(new Date(), OPS_TZ)));
  const [country, setCountry] = useState<Country | 'MIXED'>('US');
  const [niche, setNiche] = useState('');
  const [offer, setOffer] = useState('');
  const [slugRaw, setSlugRaw] = useState(DEFAULT_CALENDLY_SLUG);
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tpl: CampaignTemplate | undefined = templates.data?.find((t) => t.key === key);

  useEffect(() => {
    if (!tpl) { if (!slugTouched) setSlugRaw(DEFAULT_CALENDLY_SLUG); return; }
    setCountry(tpl.country);
    setNiche(tpl.niche);
    setOffer(tpl.offer);
    if (!name) setName(tpl.name);
    if (!slugTouched) setSlugRaw(templateCalendlySlug(tpl));
  }, [key, templates.data]);

  const blank = key === '__blank';
  const healthcare = isBlockedHealthcareCampaign(niche, country);
  const slug = normalizeCalendlySlug(slugRaw);
  // The strict cannabis lock blocks saving: a cannabis niche books your-store only, any other niche never does.
  const slugOk = !calendlySlugProblems(slug, niche || null).some((p) => p.level === 'error');
  const canSave = !!name.trim() && (!!tpl || (blank && !!niche.trim() && !!offer.trim())) && !healthcare && slugOk && !busy;

  const save = async () => {
    setBusy(true); setError(null);
    try {
      let c = await adminApi.createCampaign(blank
        ? { name: name.trim(), geoLabel: geoLabel.trim() || null, weekStart: weekStart || null, country, niche: niche.trim(), offer: offer.trim(), calendlyEventSlug: slug }
        : { templateKey: key, name: name.trim(), geoLabel: geoLabel.trim() || null, weekStart: weekStart || null, country, calendlyEventSlug: slug });
      if (c.calendlyEventSlug !== slug) {
        // the create route may not take the slug (or may apply its own default): set it explicitly
        try { c = await adminApi.updateCampaign(c.id, { calendlyEventSlug: slug }); } catch (e) {
          toast(`Created, but the Calendly event couldn't be set to ${slug}: ${errorMessage(e)} Set it on the Overview tab.`, 'warn');
        }
      }
      toast(`Campaign “${c.name}” created as a draft.`, 'ok');
      props.onClose();
      navigate(`/campaigns/${c.id}`);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="New campaign" onClose={props.onClose} width={620}>
      <div class="stack">
        <ErrorBanner error={templates.error} />
        <Field label="Start from" hint={tpl ? `${tpl.script.objections.length} objection cards · ${tpl.script.openers.length} openers · ${tpl.personas.length} practice personas` : 'Templates include the full script, windows and practice personas.'}>
          <select value={key} onChange={(e) => setKey(inputValue(e))}>
            <option value="">Pick a template…</option>
            {(templates.data ?? []).map((t) => <option value={t.key}>{t.name} ({t.country})</option>)}
            <option value="__blank">Blank campaign (write the script yourself)</option>
          </select>
        </Field>
        {templates.data && templates.data.length === 0 ? <div class="adm-muted-box">No templates are installed yet (content/campaigns). You can still start blank.</div> : null}
        <Field label="Name"><input value={name} placeholder="US plumbing · Atlanta · wk 40" onInput={(e) => setName(inputValue(e))} /></Field>
        <div class="adm-grid">
          <Field label="Geo label" hint="e.g. Ontario, Atlanta + Denver"><input value={geoLabel} onInput={(e) => setGeoLabel(inputValue(e))} /></Field>
          <Field label="Week starting (Monday)"><input type="date" value={weekStart} onInput={(e) => setWeekStart(inputValue(e))} /></Field>
          <Field label="Country">
            <select value={country} onChange={(e) => setCountry(inputValue(e) as Country | 'MIXED')}>
              <option value="US">US</option><option value="CA">Canada</option><option value="MIXED">Mixed</option>
            </select>
          </Field>
        </div>
        {blank ? (
          <div class="adm-grid">
            <Field label="Niche" hint="dispensary, plumbing_hvac…"><input value={niche} onInput={(e) => setNiche(inputValue(e))} /></Field>
            <Field label="Offer" hint="website_seo, front_desk…"><input value={offer} onInput={(e) => setOffer(inputValue(e))} /></Field>
          </div>
        ) : null}
        {tpl || blank ? (
          <CalendlySlugField value={slugRaw} onChange={(v) => { setSlugTouched(true); setSlugRaw(v); }}
            templateDefault={tpl ? templateCalendlySlug(tpl) : null} templateName={tpl?.name ?? null} niche={niche || null} />
        ) : null}
        {healthcare ? <div class="chip chip-bad" style="display:block;padding:8px 10px">Canadian healthcare facilities can't be cold called (CRTC). Pick a different niche or a US-only campaign.</div> : null}
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={!canSave} onClick={save}>{busy ? 'Creating…' : 'Create draft'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- detail

type TabKey = 'overview' | 'script' | 'windows' | 'setters' | 'leads' | 'import' | 'tools';

function CampaignDetail(props: { id: number }) {
  const me = useMe();
  const isAdmin = me.role === 'admin';
  const loaded = useLoad(() => adminApi.campaign(props.id), [props.id]);
  const [tab, setTab] = useState<TabKey>('overview');
  const c = loaded.data;
  const onSaved = (next: Campaign) => loaded.setData(next);

  if (loaded.error && !c) {
    return <div class="adm-page"><BackLink /><ErrorBanner error={loaded.error} /></div>;
  }
  if (!c) return <div class="adm-page"><BackLink /><div class="muted">Loading…</div></div>;
  const script = normalizeScript(c.script);
  const errors = scriptProblems(script, c.country).filter((p) => p.level === 'error').length;

  const tabs: Array<{ key: TabKey; label: string; badge?: string | number | null }> = isAdmin
    ? [
      { key: 'overview', label: 'Overview' },
      { key: 'script', label: 'Script', badge: errors ? `${errors} to fix` : null },
      { key: 'windows', label: 'Windows', badge: c.windows?.length ?? 0 },
      { key: 'setters', label: 'Setters', badge: c.userIds?.length ?? 0 },
      { key: 'leads', label: 'Leads', badge: c.counts?.total ?? null },
      { key: 'import', label: 'Import CSV' },
      { key: 'tools', label: 'Lookups & briefs' },
    ]
    : [{ key: 'overview', label: 'Overview' }, { key: 'script', label: 'Script' }];

  return (
    <div class="adm-page">
      <BackLink />
      <div class="adm-head">
        <div>
          <div class="row"><h1>{c.name}</h1><StatusChip status={c.status} /></div>
          <div class="adm-sub">
            {c.country}{c.geoLabel ? ` · ${c.geoLabel}` : ''} · {c.niche} · {c.offer}{c.weekStart ? ` · week of ${prettyYmd(c.weekStart)}` : ''}
            {c.templateKey ? ` · from template ${c.templateKey}` : ''}
          </div>
        </div>
      </div>
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'overview' ? (isAdmin ? <OverviewAdmin campaign={c} onSaved={onSaved} /> : <OverviewReadOnly campaign={c} />) : null}
      {tab === 'script' ? (isAdmin ? <ScriptTab campaign={c} onSaved={onSaved} /> : <ScriptView script={script} />) : null}
      {tab === 'windows' && isAdmin ? <WindowsEditor campaign={c} onSaved={onSaved} /> : null}
      {tab === 'setters' && isAdmin ? <SettersPanel campaign={c} onSaved={onSaved} /> : null}
      {tab === 'leads' && isAdmin ? <MembersTable campaign={c} /> : null}
      {tab === 'import' && isAdmin ? <ImportPanel campaignId={c.id} campaignName={c.name} onImported={() => void loaded.reload()} /> : null}
      {tab === 'tools' && isAdmin ? <div class="adm-grid-2"><LookupRunner campaignId={c.id} /><BriefRunner campaignId={c.id} /></div> : null}
    </div>
  );
}

function BackLink() {
  return <div><a href="/campaigns" onClick={(e) => { e.preventDefault(); navigate('/campaigns'); }}>← All campaigns</a></div>;
}

function Counts(props: { campaign: Campaign }) {
  const k = props.campaign.counts;
  if (!k) return null;
  return (
    <dl class="adm-kv">
      <dt>Leads</dt><dd>{k.total}</dd><dt>New</dt><dd>{k.new}</dd><dt>In progress</dt><dd>{k.inProgress}</dd>
      <dt>Callback</dt><dd>{k.callback}</dd><dt>Meeting</dt><dd>{k.meeting}</dd><dt>Closed</dt><dd>{k.closed}</dd>
    </dl>
  );
}

function OverviewReadOnly(props: { campaign: Campaign }) {
  const c = props.campaign;
  return (
    <div class="adm-grid-2">
      <div class="card stack">
        <h3>About</h3>
        <div>{normalizeScript(c.script).summary || <span class="muted">No summary.</span>}</div>
        <dl class="adm-kv">
          <dt>Max attempts</dt><dd>{c.maxAttempts} per business</dd>
          <dt>Max voicemails</dt><dd>{c.maxVms} per business</dd>
          <dt>Booking link</dt><dd class="mono">{c.calendlyEventSlug}</dd>
        </dl>
      </div>
      <div class="card stack"><h3>Leads</h3><Counts campaign={c} /></div>
    </div>
  );
}

const TRANSITIONS: Record<CampaignStatus, Array<{ to: CampaignStatus; label: string; primary?: boolean; danger?: boolean }>> = {
  draft: [{ to: 'live', label: 'Go live', primary: true }, { to: 'done', label: 'Archive' }],
  live: [{ to: 'paused', label: 'Pause' }, { to: 'done', label: 'Mark done', danger: true }],
  paused: [{ to: 'live', label: 'Resume (go live)', primary: true }, { to: 'draft', label: 'Back to draft' }, { to: 'done', label: 'Mark done', danger: true }],
  done: [],
};

function OverviewAdmin(props: { campaign: Campaign; onSaved: (c: Campaign) => void }) {
  const c = props.campaign;
  const [form, setForm] = useState({ name: c.name, geoLabel: c.geoLabel ?? '', weekStart: c.weekStart ?? '', maxAttempts: c.maxAttempts, maxVms: c.maxVms, calendlyEventSlug: c.calendlyEventSlug });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<CampaignStatus | null>(null);
  const [cloning, setCloning] = useState(false);
  useEffect(() => {
    setForm({ name: c.name, geoLabel: c.geoLabel ?? '', weekStart: c.weekStart ?? '', maxAttempts: c.maxAttempts, maxVms: c.maxVms, calendlyEventSlug: c.calendlyEventSlug });
  }, [c.id, c.updatedAt]);
  const templates = useLoad(() => (c.templateKey ? adminApi.templates() : Promise.resolve([] as CampaignTemplate[])), [c.templateKey]);
  const tpl = c.templateKey ? templates.data?.find((t) => t.key === c.templateKey) : undefined;
  // null = no template (blank campaign); while templates load, fall back to the app default
  const templateDefault = c.templateKey ? templateCalendlySlug(tpl) : null;
  const slug = normalizeCalendlySlug(form.calendlyEventSlug);
  const dirty = form.name !== c.name || form.geoLabel !== (c.geoLabel ?? '') || form.weekStart !== (c.weekStart ?? '')
    || form.maxAttempts !== c.maxAttempts || form.maxVms !== c.maxVms || slug !== c.calendlyEventSlug;
  useUnsavedWarning(dirty);
  // only active users with role 'setter' count for go-live (CORE); while users load, any assigned user counts
  const users = useLoad(() => adminApi.users(), []);
  const ownProblems = useMemo(() => goLiveProblems({ script: normalizeScript(c.script), userIds: c.userIds ?? [], country: c.country, users: users.data }), [c, users.data]);
  // once the basics pass, the server's own go-live check (CONTENT house rules), so Go live isn't a guess
  const houseProblems = useMemo(() => (ownProblems.length ? [] : houseRuleProblems(c)), [c, ownProblems.length]);
  const liveProblems = ownProblems.length ? ownProblems : houseProblems;
  const setterCount = users.data ? activeSetterIds(c.userIds ?? [], users.data).length : (c.userIds ?? []).length;

  const formProblems: string[] = [];
  if (!form.name.trim()) formProblems.push('The name can’t be empty.');
  if (!Number.isInteger(form.maxAttempts) || form.maxAttempts < 1 || form.maxAttempts > 12) formProblems.push('Max attempts must be a whole number from 1 to 12.');
  if (!Number.isInteger(form.maxVms) || form.maxVms < 0 || form.maxVms > form.maxAttempts) formProblems.push('Max voicemails must be a whole number from 0 up to max attempts.');
  formProblems.push(...calendlySlugProblems(slug, c.niche).filter((p) => p.level === 'error').map((p) => p.message));

  const save = async () => {
    if (formProblems.length) { setError(formProblems[0]); return; }
    setBusy(true); setError(null);
    try {
      props.onSaved(await adminApi.updateCampaign(c.id, {
        name: form.name.trim(), geoLabel: form.geoLabel.trim() || null, weekStart: form.weekStart || null,
        maxAttempts: form.maxAttempts, maxVms: form.maxVms, calendlyEventSlug: slug,
      }));
      toast('Campaign saved.', 'ok');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  const setStatus = async (to: CampaignStatus) => {
    props.onSaved(await adminApi.updateCampaign(c.id, { status: to }));
    toast(`Campaign is now ${to}.`, 'ok');
  };

  return (
    <div class="adm-grid-2">
      <div class="card stack">
        <h3>Settings</h3>
        <Field label="Name"><input value={form.name} onInput={(e) => setForm({ ...form, name: inputValue(e) })} /></Field>
        <div class="adm-grid">
          <Field label="Geo label"><input value={form.geoLabel} onInput={(e) => setForm({ ...form, geoLabel: inputValue(e) })} /></Field>
          <Field label="Week starting"><input type="date" value={form.weekStart} onInput={(e) => setForm({ ...form, weekStart: inputValue(e) })} /></Field>
          <Field label="Max attempts per business" hint="Across the campaign; 4 h apart, ≤ 2 a day, ≤ 3 a week (enforced).">
            <input type="number" min={1} max={12} value={form.maxAttempts} onInput={(e) => setForm({ ...form, maxAttempts: Number(inputValue(e)) })} />
          </Field>
          <Field label="Max voicemails per business" hint="Live + dropped.">
            <input type="number" min={0} max={form.maxAttempts} value={form.maxVms} onInput={(e) => setForm({ ...form, maxVms: Number(inputValue(e)) })} />
          </Field>
        </div>
        <CalendlySlugField value={form.calendlyEventSlug} onChange={(v) => setForm({ ...form, calendlyEventSlug: v })}
          templateDefault={templateDefault} templateName={tpl?.name ?? c.templateKey} niche={c.niche} />
        {dirty && formProblems.length ? <ul class="adm-problems">{formProblems.map((p) => <li class="error">{p}</li>)}</ul> : null}
        <ErrorBanner error={error} />
        <div class="row">
          <button class="btn btn-primary" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          <button class="btn" disabled={!dirty || busy} onClick={() => setForm({ name: c.name, geoLabel: c.geoLabel ?? '', weekStart: c.weekStart ?? '', maxAttempts: c.maxAttempts, maxVms: c.maxVms, calendlyEventSlug: c.calendlyEventSlug })}>Revert</button>
        </div>
      </div>

      <div class="stack" style="gap:14px">
        <div class="card stack">
          <div class="row"><h3 style="margin:0">Status</h3><StatusChip status={c.status} /></div>
          {c.status === 'draft' || c.status === 'paused' ? (
            liveProblems.length
              ? <div class="stack">
                  <div class="adm-sub">{ownProblems.length ? 'Before this can go live:' : 'The server’s go-live check (house rules for scripts and windows) will refuse this until:'}</div>
                  <ProblemList problems={liveProblems.map((m) => ({ level: 'error', where: '', message: m }))} max={8} />
                </div>
              : <div class="chip chip-ok" style="display:block;padding:8px 10px">Ready to go live: script complete and {setterCount} active setter{setterCount === 1 ? '' : 's'} assigned.</div>
          ) : null}
          {c.status === 'done' ? <div class="adm-sub">Done campaigns stay for reporting. Clone it to run the same niche again.</div> : null}
          <div class="row" style="flex-wrap:wrap">
            {TRANSITIONS[c.status].map((t) => (
              <button class={`btn ${t.primary ? 'btn-primary' : t.danger ? 'btn-danger' : ''}`}
                disabled={t.to === 'live' && liveProblems.length > 0}
                title={t.to === 'live' && liveProblems.length ? liveProblems[0] : undefined}
                onClick={() => setConfirm(t.to)}>{t.label}</button>
            ))}
            <button class="btn" onClick={() => setCloning(true)}>Clone to next week…</button>
          </div>
        </div>
        <div class="card stack">
          <h3>Leads</h3>
          <Counts campaign={c} />
          <dl class="adm-kv">
            <dt>Created</dt><dd>{fmtDateTime(c.createdAt)}</dd>
            <dt>Updated</dt><dd>{fmtDateTime(c.updatedAt)}</dd>
            {c.clonedFrom ? <><dt>Cloned from</dt><dd><a href={`/campaigns/${c.clonedFrom}`} onClick={(e) => { e.preventDefault(); navigate(`/campaigns/${c.clonedFrom}`); }}>campaign #{c.clonedFrom}</a></dd></> : null}
          </dl>
        </div>
      </div>

      {confirm ? (
        <ConfirmModal
          title={confirm === 'live' ? 'Go live?' : confirm === 'paused' ? 'Pause this campaign?' : confirm === 'done' ? 'Mark this campaign done?' : 'Move back to draft?'}
          body={confirm === 'live'
            ? <span>Assigned setters can start dialling <b>{c.name}</b> as soon as they are certified. Every call still passes the compliance gate.</span>
            : confirm === 'paused' ? <span>Setters won't get new leads from this campaign until it's resumed. Callbacks stay with their owners.</span>
            : confirm === 'done' ? <span>No more dialling on this campaign. Its numbers stay in reports. This can't be undone from the app (clone it to run again).</span>
            : <span>Setters stop getting leads and you can edit it freely.</span>}
          confirmLabel={TRANSITIONS[c.status].find((t) => t.to === confirm)?.label ?? 'Confirm'}
          danger={confirm === 'done'}
          onConfirm={() => setStatus(confirm)}
          onClose={() => setConfirm(null)}
        />
      ) : null}
      {cloning ? <CloneModal campaign={c} onClose={() => setCloning(false)} /> : null}
    </div>
  );
}

function CloneModal(props: { campaign: Campaign; onClose: () => void }) {
  const c = props.campaign;
  const week = nextMonday(c.weekStart ?? ymdInTz(new Date(), OPS_TZ));
  const [name, setName] = useState(`${c.name.replace(/\s*\(week of [^)]*\)\s*$/i, '')} (week of ${week})`);
  const [weekStart, setWeekStart] = useState(week);
  const [copyOpenLeads, setCopyOpenLeads] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setBusy(true); setError(null);
    try {
      const next = await adminApi.cloneCampaign(c.id, { name: name.trim(), weekStart, copyOpenLeads });
      toast(`Cloned as “${next.name}” (draft).`, 'ok');
      props.onClose();
      navigate(`/campaigns/${next.id}`);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Clone to next week" onClose={props.onClose}>
      <div class="stack">
        <div class="adm-sub">Copies the script, windows, limits, setters and practice personas into a new draft.</div>
        <Field label="Name"><input value={name} onInput={(e) => setName(inputValue(e))} /></Field>
        <Field label="Week starting"><input type="date" value={weekStart} onInput={(e) => setWeekStart(inputValue(e))} /></Field>
        <Check checked={copyOpenLeads} onChange={setCopyOpenLeads} label="Also copy leads that are still open (new, in progress, callback) as fresh leads" />
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={busy || !name.trim() || !weekStart} onClick={go}>{busy ? 'Cloning…' : 'Clone'}</button>
        </div>
      </div>
    </Modal>
  );
}

function ScriptTab(props: { campaign: Campaign; onSaved: (c: Campaign) => void }) {
  const c = props.campaign;
  const saved = useMemo(() => normalizeScript(c.script), [c.id, c.updatedAt]);
  const [draft, setDraft] = useState<CampaignScript>(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  useEffect(() => { setDraft(saved); }, [saved]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  useUnsavedWarning(dirty);
  const problems = useMemo(() => scriptProblems(draft, c.country), [draft, c.country]);
  const errors = problems.filter((p) => p.level === 'error');

  const save = async () => {
    if (c.status === 'live' && errors.length) {
      setError(`This campaign is live, so the script can't be saved with problems: ${errors[0].message}`);
      return;
    }
    setBusy(true); setError(null);
    try {
      props.onSaved(await adminApi.updateCampaign(c.id, { script: draft }));
      toast('Script saved.', 'ok');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div class="stack" style="gap:12px">
      <div class="card stack" style="position:sticky;top:0;z-index:5">
        <div class="adm-toolbar">
          <b>{errors.length ? `${errors.length} problem${errors.length === 1 ? '' : 's'} to fix` : 'No blocking problems'}</b>
          <span class="adm-sub">{problems.length - errors.length} warning{problems.length - errors.length === 1 ? '' : 's'}</span>
          <span class="grow" />
          <Check checked={preview} onChange={setPreview} label="Preview as setter" />
          <button class="btn" disabled={!dirty || busy} onClick={() => setDraft(saved)}>Revert</button>
          <button class="btn btn-primary" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : dirty ? 'Save script' : 'Saved'}</button>
        </div>
        <ErrorBanner error={error} />
        <details>
          <summary class="adm-sub" style="cursor:pointer">Show {problems.length} check{problems.length === 1 ? '' : 's'}</summary>
          <div style="margin-top:8px;max-height:220px;overflow:auto"><ProblemList problems={problems} /></div>
        </details>
      </div>
      {preview ? <ScriptView script={draft} /> : <ScriptEditor value={draft} onChange={setDraft} country={c.country} />}
    </div>
  );
}
